import {
    Box,
    Button,
    CircularProgress,
    DateRangePicker,
    FormLabel,
    Grid,
    InputAdornment,
    Link,
    Typography,
} from '@deere/fuel-react';
import DownloadIcon from '@deere/ux.brand-foundations/icons/download.svg';
import { CalendarMonth } from '@mui/icons-material';
import { Breadcrumbs } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { LoggedInContext } from '../../context/LoggedInContext';
import { CurrentBidHistoryView } from './CurrentBidHistoryView';
import { LegacyBidHistoryView } from './LegacyBidHistoryView';
import { useGoBack } from '../../hooks/useGoBack';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from './EmployeeInfo.module.css';

export const EmployeeInfo = () => {
    const { racf, jobNumber } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const [employeeJobBids, setEmployeeJobBids] = useState([]);
    const [viewMode, setViewMode] = useState('current');

    const start = DateTime.now().minus({ months: 3 }).toJSDate();
    const end = DateTime.now().toJSDate();
    const [dateRange, setDateRange] = useState([start, end]);
    const { makeRequest, authState } = useContext(LoggedInContext);

    const fetchBidHistoryFromEndpoint = async (
        endpointUrl,
        notFoundMessage
    ) => {
        setIsLoading(true);
        try {
            const response = await makeRequest(endpointUrl);
            if (response.status === 200) {
                const empBidDetails = await response.json();
                setEmployeeJobBids(empBidDetails);
                setAlertVariant(false);
            } else {
                setEmployeeJobBids([]);
                setAlertVariant('error');
                if (response.status === 404) {
                    setAlertMsg(notFoundMessage);
                } else {
                    setAlertMsg(
                        errorBannerFromResponse(
                            response,
                            'We are unable to retrieve information for this employee. Please try again later.'
                        ).message
                    );
                }
            }
        } catch (err) {
            setAlertMsg(
                errorBannerFromResponse(
                    err,
                    'We are unable to retrieve information for this employee. Please try again later.'
                ).message
            );
            setAlertVariant('error');
        } finally {
            setIsLoading(false);
        }
    };

    const buildCurrentSystemUrl = (startDateIso, endDateIso) =>
        process.env.REACT_APP_API_ENDPOINT +
        '/bidHistory/' +
        racf +
        '?startDate=' +
        startDateIso +
        '&endDate=' +
        endDateIso;

    const loadCurrentBidHistory = async (rangeStart, rangeEnd) => {
        const startDate =
            DateTime.fromJSDate(rangeStart).toFormat('yyyy-LL-dd');
        const endDate = DateTime.fromJSDate(rangeEnd).toFormat('yyyy-LL-dd');
        await fetchBidHistoryFromEndpoint(
            buildCurrentSystemUrl(startDate, endDate),
            'There are no job bids for this employee for this date range.'
        );
    };

    const dateRangeHandler = async (dates) => {
        if (!dates[1]) {
            return;
        }
        setDateRange(dates);
        setViewMode('current');
        await loadCurrentBidHistory(dates[0], dates[1]);
    };

    const toggleBidHistorySource = async () => {
        if (viewMode === 'current') {
            setViewMode('legacy');
            await fetchBidHistoryFromEndpoint(
                process.env.REACT_APP_API_ENDPOINT +
                    '/bidHistory/' +
                    racf +
                    '/legacy',
                'There is no old system bid history for this employee.'
            );
        } else {
            setViewMode('current');
            await loadCurrentBidHistory(dateRange[0], dateRange[1]);
        }
    };

    useEffect(() => {
        let ignore = false;
        setViewMode('current');
        const fetchEmployeeBidDetails = async () => {
            setIsLoading(true);

            const threeMonthsAgo = DateTime.now()
                .minus({ months: 3 })
                .toISODate();
            const today = DateTime.now().toISODate();
            try {
                const response = await makeRequest(
                    process.env.REACT_APP_API_ENDPOINT +
                        '/bidHistory/' +
                        racf +
                        '?startDate=' +
                        threeMonthsAgo +
                        '&endDate=' +
                        today
                );
                if (!ignore) {
                    if (response.status === 200) {
                        const empBidDetails = await response.json();
                        setEmployeeJobBids(empBidDetails);
                        setAlertVariant(false);
                    } else {
                        setEmployeeJobBids([]);
                        setAlertVariant('error');
                        if (response.status === 404) {
                            setAlertMsg(
                                'There are no job bids for this employee for this date range.'
                            );
                        } else {
                            setAlertMsg(
                                errorBannerFromResponse(
                                    response,
                                    'We are unable to retrieve information for this employee. Please try again later.'
                                ).message
                            );
                        }
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are unable to retrieve information for this employee. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmployeeBidDetails();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, makeRequest, racf]);

    const getBreadcrumbsUrl = (jobBids) => {
        const jobBid = jobBids.find((element) => {
            return element.jobNumber === jobNumber;
        });
        let jobDetailPageUrl = '/active-jobs/';
        if (jobBid) {
            const isActiveJob =
                DateTime.now().toISODate() <
                DateTime.fromISO(jobBid.endDate).toFormat('yyyy-LL-dd');
            jobDetailPageUrl = isActiveJob ? '/active-jobs/' : '/closed-jobs/';
        }
        return jobDetailPageUrl;
    };

    const jobDetailUrl = getBreadcrumbsUrl(employeeJobBids) + jobNumber;
    const goBack = useGoBack(jobDetailUrl);

    const downloadFileFromEndpoint = async (
        endpointUrl,
        fallbackFilename,
        errorMessage
    ) => {
        try {
            const response = await makeRequest(endpointUrl);
            if (response.ok) {
                const contentDisposition =
                    response.headers.get('content-disposition') || '';
                const filenameMatch = contentDisposition.split('filename=')[1];
                const filename = filenameMatch
                    ? filenameMatch.replace(/["']/g, '').trim()
                    : fallbackFilename;
                const fileBlob = await response.blob();
                const fileURL = window.URL.createObjectURL(fileBlob);
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = filename;
                link.target = '_blank';
                link.click();
                window.URL.revokeObjectURL(fileURL);
                link.remove();
            } else {
                setAlertMsg(
                    errorBannerFromResponse(response, errorMessage).message
                );
                setAlertVariant('error');
            }
        } catch (err) {
            setAlertMsg(errorBannerFromResponse(err, errorMessage).message);
            setAlertVariant('error');
        }
    };

    const exportCurrentBidHistory = async () => {
        if (employeeJobBids.length === 0) {
            return;
        }
        const startDate = DateTime.fromJSDate(dateRange[0]).toFormat(
            'yyyy-LL-dd'
        );
        const endDate = DateTime.fromJSDate(dateRange[1]).toFormat(
            'yyyy-LL-dd'
        );
        await downloadFileFromEndpoint(
            process.env.REACT_APP_API_ENDPOINT +
                '/bidHistory/' +
                racf +
                '/download?startDate=' +
                startDate +
                '&endDate=' +
                endDate,
            'bid-history-' +
                String(racf).toUpperCase() +
                '-' +
                startDate +
                '-to-' +
                endDate +
                '.xlsx',
            'We are unable to download the bid history for this employee.'
        );
    };

    const exportLegacyBidHistory = async () => {
        if (employeeJobBids.length === 0) {
            return;
        }
        await downloadFileFromEndpoint(
            process.env.REACT_APP_API_ENDPOINT +
                '/bidHistory/' +
                racf +
                '/legacy/download',
            'bid-history-legacy-' + String(racf).toUpperCase() + '.xlsx',
            'We are unable to download the old system bid history for this employee.'
        );
    };

    return (
        <div className={styles.parentContainer}>
            <Box spacing={6} className={styles.contentToContainer}>
                {jobNumber && (
                    <Breadcrumbs aria-label="Breadcrumbs">
                        <Link href={jobDetailUrl} onClick={goBack}>
                            Job# {jobNumber}
                        </Link>
                        <Typography color="textPrimary">
                            Employee Information
                        </Typography>
                    </Breadcrumbs>
                )}
                <Box className={styles.spaceOnTop}>
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                {!isLoading && (
                    <Box className={styles.boxForButton}>
                        <Grid>
                            <Typography variant="h1" className={styles.heading}>
                                {viewMode === 'legacy'
                                    ? 'Old System Bid History'
                                    : 'Employee Information'}
                            </Typography>
                        </Grid>
                        <Grid className={styles.dateAndExport}>
                            {viewMode === 'current' && (
                                <Grid>
                                    <FormLabel required={true}>
                                        Choose Date Range
                                    </FormLabel>
                                    <DateRangePicker
                                        value={dateRange}
                                        onChange={(dates) => {
                                            dateRangeHandler(dates);
                                        }}
                                        startDatePlaceholder="Select Start Date"
                                        endDatePlaceholder="Select End Date"
                                        StartInputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarMonth />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            )}
                            <Button
                                variant="primary"
                                disabled={employeeJobBids.length === 0}
                                startIcon={<img alt="" src={DownloadIcon} />}
                                onClick={
                                    viewMode === 'legacy'
                                        ? exportLegacyBidHistory
                                        : exportCurrentBidHistory
                                }
                            >
                                Export
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={toggleBidHistorySource}
                            >
                                {viewMode === 'current'
                                    ? 'Old System History'
                                    : 'Back to Current System'}
                            </Button>
                        </Grid>
                    </Box>
                )}

                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}

                {!isLoading && employeeJobBids.length > 0 && (
                    <Grid
                        container
                        spacing={4}
                        className={styles.bidGridSection}
                    >
                        <Grid item sm={6}>
                            <Typography
                                fontWeight="700"
                                variant="h2"
                                color="#000000"
                            >
                                {employeeJobBids[0].name} -{' '}
                                {employeeJobBids[0].racfId.toUpperCase()}
                            </Typography>
                        </Grid>
                        <Grid item sm={6} className={styles.bidCountSection}>
                            <span>{employeeJobBids.length} job bids</span>
                        </Grid>

                        <Grid className={styles.jobBidInfoSection} item sm={12}>
                            <span>
                                <b>Dept: </b>
                                {employeeJobBids[0].dept}
                            </span>
                            <span>
                                <b>Current Classification: </b>
                                {employeeJobBids[0].classification}
                            </span>
                            {viewMode === 'current' && (
                                <span>
                                    <b>Seniority: </b>
                                    {employeeJobBids[0].seniority
                                        ? DateTime.fromISO(
                                              employeeJobBids[0].seniority
                                          ).toFormat('LL/dd/yy')
                                        : ''}
                                </span>
                            )}
                            {viewMode === 'legacy' && (
                                <span>
                                    <b>Employee Number: </b>
                                    {employeeJobBids[0].employeeNumber}
                                </span>
                            )}
                        </Grid>
                    </Grid>
                )}

                {!isLoading &&
                    employeeJobBids.length > 0 &&
                    (viewMode === 'legacy' ? (
                        <LegacyBidHistoryView bids={employeeJobBids} />
                    ) : (
                        <CurrentBidHistoryView bids={employeeJobBids} />
                    ))}
            </Box>
        </div>
    );
};
